// @givmo/app-admin — the Gift cards → PROGRAMS tab (EP-BE8-web).
//
// Where a company authors standing occasion rules ("every work anniversary → a $25 gift card") and
// watches whether the daily unattended sweep is actually delivering them. This is the portal side of
// EP-BE8 — the ONLY place company money moves with no human present at spend time — so the surface is
// weighted towards the guards, not the CRUD: what is owed and undelivered, and whether the funding will
// cover what is coming.
//
// ── THE TWO SWITCHES, AND WHY A 404 IS NOT AN ERROR ──────────────────────────────────────────────
//
// The tab renders only when `isCompanyOccasionProgramsEnabled()`, inside a page already dark behind
// `isCompanyGiftCardsEnabled()`. Those flip independently of the BACKEND's
// `COMPANY_OCCASION_PROGRAMS_ENABLED`, which 404s every occasion route while off — and gift cards may
// legitimately go live first. So a 404 from the reads renders `OccasionUnavailable`, a considered
// state, never a toast or a broken panel. Both layers are test-pinned.
//
// ── NO CHARGE UI ─────────────────────────────────────────────────────────────────────────────────
//
// Programs auto-charge server-side on the sweep (D-EP-29). Nothing on this surface initiates a charge,
// so there is deliberately no confirm-before-charge dialog and no idempotency-key holder — unlike the
// attended send/batch flows in this same feature, which have both and must keep them.
//
// ── DELIVERY-ONLY (D-EP-17) ──────────────────────────────────────────────────────────────────────
//
// No redemption state anywhere: no "unclaimed" count exists on the wire and none may be derived.

import { useCallback, useMemo, useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  type QueryFunctionContext,
} from '@tanstack/react-query';
import {
  sdk,
  isGivmoError,
  type CompanyOccasionProgramOut,
  type CompanyOccasionProgramPreflightOut,
} from '@givmo/api-client';
import { toDateOnly } from '@givmo/api-client/list';
import { companiesGetCompanyOccasionPreflightOptions } from '@givmo/api-client/query';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  EmptyState,
  Icon,
  Meter,
  Skeleton,
  testIdProp,
  toast,
  type DataTableColumn,
} from '@givmo/design-system';
import { AlertTriangle, CalendarHeart, Plus } from 'lucide-react';
import { formatCentsUsd } from '../dashboard/dashboard-decode';
import { useEmployerModulePaths } from '../../lib/module-paths';
import { OccasionProgramDialog } from './occasion-program-dialog';
import { OccasionExecutionsView } from './occasion-executions-view';
import { OccasionUnavailable } from './occasion-unavailable';
import {
  extractOccasionPrograms,
  formatOccasionCents,
  isOccasionConflictError,
  isOccasionUnavailableError,
  occasionFundingLabel,
  occasionTypeDescription,
  occasionTypeLabel,
  PAUSE_FREEZES_HELD_COPY,
  preflightCoverage,
  PREFLIGHT_ADVISORY_COPY,
  programStatusLabel,
  programStatusTone,
  receiptRecipientLabel,
  RESUME_NO_BACKCHARGE_COPY,
} from './occasion-programs-decode';

const PREFIX = 'gift-card-programs';
const PROGRAMS_PAGE_LIMIT = 20;
const PROGRAMS_LIST_KEY = ['companies-list_company_occasion_programs', 'panel'] as const;

async function fetchProgramsPage(
  offset: number,
  signal: AbortSignal,
): Promise<CompanyOccasionProgramOut[]> {
  const { data } = await sdk.companiesListCompanyOccasionPrograms({
    query: { 'page[limit]': PROGRAMS_PAGE_LIMIT, 'page[offset]': offset },
    signal,
    throwOnError: true,
  });
  return extractOccasionPrograms(data);
}

/** The program a drill-in is showing, carried so the activity header can name it without a re-fetch. */
interface SelectedProgram {
  id: string;
  occasionType: string;
  paused: boolean;
}

export interface OccasionProgramsPanelProps {
  /**
   * Hand off to the Batches tab so an admin can see how a gift was actually delivered and charged.
   * Optional: without it the executions detail simply renders no link (the panel stays usable on its
   * own), which is what keeps this tab from depending on its host's tab state.
   */
  onOpenBatch?: (batchId: string) => void;
}

export function OccasionProgramsPanel({ onOpenBatch }: OccasionProgramsPanelProps = {}) {
  const { words } = useEmployerModulePaths();
  const [dialogProgram, setDialogProgram] = useState<CompanyOccasionProgramOut | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState<SelectedProgram | null>(null);

  const list = useInfiniteQuery({
    queryKey: PROGRAMS_LIST_KEY,
    queryFn: ({ pageParam, signal }: QueryFunctionContext<typeof PROGRAMS_LIST_KEY, number>) =>
      fetchProgramsPage(pageParam, signal),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < PROGRAMS_PAGE_LIMIT
        ? undefined
        : allPages.reduce((n, p) => n + p.length, 0),
    retry: false,
  });

  const preflight = useQuery({
    ...companiesGetCompanyOccasionPreflightOptions(),
    retry: false,
  });

  const programs = useMemo(() => (list.data ? list.data.pages.flat() : []), [list.data]);

  const refresh = () => {
    void list.refetch();
    void preflight.refetch();
  };

  const pause = useMutation({
    mutationFn: (programId: string) =>
      sdk.companiesPauseCompanyOccasionProgram({
        path: { program_id: programId },
        throwOnError: true,
      }),
    onSuccess: () => {
      toast.success('Program paused');
      refresh();
    },
    onError: (error: unknown) =>
      toast.error(isGivmoError(error) ? error.message : 'We couldn’t pause this program.'),
  });

  const resume = useMutation({
    mutationFn: (programId: string) =>
      sdk.companiesResumeCompanyOccasionProgram({
        path: { program_id: programId },
        throwOnError: true,
      }),
    onSuccess: () => {
      toast.success('Program resumed');
      refresh();
    },
    onError: (error: unknown) => {
      // 409 = a replacement program already covers this occasion. Say which, rather than "try again".
      if (isOccasionConflictError(error)) {
        toast.error(error.message);
        return;
      }
      toast.error(isGivmoError(error) ? error.message : 'We couldn’t resume this program.');
    },
  });

  // The environment simply doesn't have the feature on — a considered state, not a failure.
  // ── Hooks below run on EVERY render, so they sit above the two early returns; a hook behind a
  // conditional return changes the hook count between renders and React throws. ─────────────
  // Stable so the column set that closes over it can be, which is what lets the rows skip re-render.
  const openManage = useCallback((program: CompanyOccasionProgramOut) => {
    setDialogProgram(program);
    setDialogOpen(true);
  }, []);

  const busyId = pause.isPending
    ? pause.variables
    : resume.isPending
      ? resume.variables
      : undefined;

  // Memoized so rows skip re-render on unrelated parent state. The deps are exactly what the
  // cells close over, so a real change still rebuilds them.
  const columns: DataTableColumn<CompanyOccasionProgramOut>[] = useMemo(
    () => [
      {
        id: 'occasion',
        header: 'Occasion',
        width: '28%',
        cell: (p) => (
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-w600 text-text-strong">
              {occasionTypeLabel(p.occasion_type)}
            </span>
            <span className="truncate text-s12 text-text-tertiary">
              {occasionTypeDescription(p.occasion_type)}
            </span>
          </div>
        ),
      },
      {
        id: 'amount',
        header: 'Gift amount',
        width: '140px',
        // Labelled as the CURRENT rule on purpose: it is what future gifts will be worth, not what past
        // ones were charged (each of those carries its own pinned price on the activity view).
        cell: (p) => (
          <div className="flex flex-col">
            <span className="tabular-nums text-text-strong">
              {formatCentsUsd(p.denomination_cents)}
            </span>
            <span className="text-s12 text-text-tertiary">current rule</span>
          </div>
        ),
      },
      {
        id: 'funding',
        header: 'Funding',
        width: '150px',
        cell: (p) => (
          <span className="text-text-secondary">{occasionFundingLabel(p.funding_source)}</span>
        ),
      },
      {
        id: 'designee',
        header: 'Receipts to',
        width: '140px',
        // A wallet program issues no receipt at all (the deposit was already receipted), so it names no
        // recipient — see `receiptRecipientLabel`.
        cell: (p) => (
          <span className="text-text-secondary" {...testIdProp(`${PREFIX}-receipts-${p.id}`)}>
            {receiptRecipientLabel(p, words)}
          </span>
        ),
      },
      {
        id: 'created',
        header: 'Created',
        width: '110px',
        cell: (p) => <span className="text-text-secondary">{toDateOnly(p.created_at) || '—'}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        width: '110px',
        cell: (p) => (
          <Badge tone={programStatusTone(p.status)} dot testId={`${PREFIX}-status-${p.id}`}>
            {programStatusLabel(p.status)}
          </Badge>
        ),
      },
      {
        id: 'actions',
        header: '',
        width: '260px',
        cell: (p) => {
          const paused = p.status === 'PAUSED';
          return (
            <div className="flex flex-wrap items-center justify-end gap-8">
              <Button
                appearance="saas"
                tone="ghost"
                size="sm"
                onClick={() => setSelected({ id: p.id, occasionType: p.occasion_type, paused })}
                testId={`${PREFIX}-activity-${p.id}`}
              >
                Activity
              </Button>
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() => openManage(p)}
                testId={`${PREFIX}-manage-${p.id}`}
              >
                Manage
              </Button>
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                disabled={busyId === p.id}
                onClick={() => (paused ? resume.mutate(p.id) : pause.mutate(p.id))}
                testId={`${PREFIX}-${paused ? 'resume' : 'pause'}-${p.id}`}
              >
                {paused ? 'Resume' : 'Pause'}
              </Button>
            </div>
          );
        },
      },
    ],
    [busyId, openManage, pause, resume, words],
  );

  if (list.isError && isOccasionUnavailableError(list.error)) {
    return (
      <div className="flex flex-col gap-24" {...testIdProp(PREFIX)}>
        <OccasionUnavailable />
      </div>
    );
  }

  if (selected !== null) {
    return (
      <div className="flex flex-col gap-24" {...testIdProp(PREFIX)}>
        <OccasionExecutionsView
          programId={selected.id}
          occasionType={selected.occasionType}
          paused={selected.paused}
          onBack={() => setSelected(null)}
          onOpenBatch={onOpenBatch}
        />
      </div>
    );
  }

  const openCreate = () => {
    setDialogProgram(null);
    setDialogOpen(true);
  };

  const hasPaused = programs.some((p) => p.status === 'PAUSED');

  return (
    <div className="flex flex-col gap-24" {...testIdProp(PREFIX)}>
      <PreflightPanel data={preflight.data} isPending={preflight.isPending} />

      <div className="flex flex-wrap items-center justify-end gap-12">
        <Button
          appearance="saas"
          tone="primary"
          size="md"
          leadingIcon={<Icon icon={Plus} size={16} aria-hidden />}
          onClick={openCreate}
          testId={`${PREFIX}-create`}
        >
          New program
        </Button>
      </div>

      <DataTable
        testId={`${PREFIX}-table`}
        columns={columns}
        rows={programs}
        rowKey={(p) => p.id}
        rowTestId={(p) => `${PREFIX}-row-${p.id}`}
        loading={list.isPending}
        loadingTestId={`${PREFIX}-loading`}
        error={
          list.isError
            ? isGivmoError(list.error)
              ? list.error.message
              : 'Failed to load occasion programs.'
            : undefined
        }
        errorTestId={`${PREFIX}-error`}
        retryTestId={`${PREFIX}-error-retry`}
        onRetry={() => void list.refetch()}
        emptyState={
          <EmptyState
            testId={`${PREFIX}-empty-state`}
            icon={<Icon icon={CalendarHeart} size={20} aria-hidden />}
            title="No occasion programs yet"
            description="Set up a standing gift for birthdays, work anniversaries or new hires, and Givmo sends it for you."
            action={
              <Button
                appearance="saas"
                tone="primary"
                size="sm"
                onClick={openCreate}
                testId={`${PREFIX}-empty-create`}
              >
                New program
              </Button>
            }
          />
        }
        footer={
          list.hasNextPage ? (
            <>
              <span className="font-sans text-s13 tracking-normal text-text-tertiary tabular-nums">
                Showing {programs.length} programs
              </span>
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                disabled={list.isFetchingNextPage}
                onClick={() => void list.fetchNextPage()}
                testId={`${PREFIX}-load-more`}
              >
                {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </>
          ) : undefined
        }
      />

      {/* Pause/resume both mean something other than what they look like — state it where they live. */}
      {hasPaused && (
        <div
          className="flex flex-col gap-6 rounded-lg border border-border-subtle bg-surface-sunken p-12"
          {...testIdProp(`${PREFIX}-pause-note`)}
        >
          <p className="font-sans text-s13 leading-h15 tracking-givmo text-text-secondary">
            {PAUSE_FREEZES_HELD_COPY}
          </p>
          <p className="font-sans text-s13 leading-h15 tracking-givmo text-text-secondary">
            {RESUME_NO_BACKCHARGE_COPY}
          </p>
        </div>
      )}

      <OccasionProgramDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        program={dialogProgram}
        onSaved={refresh}
      />
    </div>
  );
}

// ── Funding preflight (D-EP-28) ──────────────────────────────────────────────────────────────────

function PreflightPanel({
  data,
  isPending,
}: {
  data: CompanyOccasionProgramPreflightOut | undefined;
  isPending: boolean;
}) {
  if (isPending) {
    return (
      <Skeleton
        className="h-[140px] w-full rounded-lg"
        {...testIdProp(`${PREFIX}-preflight-loading`)}
      />
    );
  }

  // An error (including the flag-off 404) is the environment, not a failure of this panel — it renders
  // nothing and the list's own unavailable state carries the message.
  if (data === undefined) return null;

  const held = data.held_execution_count ?? 0;
  const items = data.programs ?? [];

  return (
    <Card>
      <CardHeader
        title="Coming up"
        subtitle={`Occasions in the next ${data.horizon_days} days, and whether their funding looks like it will cover them.`}
      />
      <CardBody className="flex flex-col gap-16">
        {/* ★ HELD leads: a non-zero count is money the company owes its employees and did not pay. */}
        {held > 0 && (
          <div
            className="flex items-start gap-8 rounded-lg border border-danger-100 bg-danger-50 p-12"
            {...testIdProp(`${PREFIX}-held-banner`)}
          >
            <Icon icon={AlertTriangle} size={16} aria-hidden className="mt-1 text-danger-700" />
            <p className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-danger-700">
              {held} gift{held === 1 ? '' : 's'} could not be sent and {held === 1 ? 'is' : 'are'}{' '}
              still owed. Open a program’s Activity to see why — they retry automatically while the
              program is active.
            </p>
          </div>
        )}

        {items.length === 0 ? (
          <p
            className="font-sans text-s13 tracking-givmo text-text-secondary"
            {...testIdProp(`${PREFIX}-preflight-empty`)}
          >
            No occasions are coming up in this window.
          </p>
        ) : (
          <div className="flex flex-col gap-16" {...testIdProp(`${PREFIX}-preflight`)}>
            {items.map((item) => {
              const coverage = preflightCoverage(item);
              return (
                <div key={item.program_id} className="flex flex-col gap-6">
                  <Meter
                    value={coverage.percent}
                    max={100}
                    tone={coverage.tone}
                    label={`${occasionTypeLabel(item.occasion_type)} — ${item.upcoming_occasion_count} coming up`}
                    valueLabel={`${formatOccasionCents(item.upcoming_total_cents)} needed`}
                    testId={`${PREFIX}-preflight-meter-${item.program_id}`}
                  />
                  <p
                    className="font-sans text-s12 tracking-givmo text-text-tertiary"
                    {...testIdProp(`${PREFIX}-preflight-detail-${item.program_id}`)}
                  >
                    {coverage.availableCents !== null
                      ? `${formatOccasionCents(coverage.availableCents)} available in the company wallet.`
                      : item.has_saved_card === true
                        ? 'Charged to the saved company card.'
                        : 'No saved company card — gifts for this program will be held.'}
                    {coverage.atRisk && (
                      <span
                        className="text-warning-700"
                        {...testIdProp(`${PREFIX}-preflight-warning-${item.program_id}`)}
                      >
                        {' '}
                        This may not cover the occasions coming up.
                      </span>
                    )}
                  </p>
                </div>
              );
            })}

            <p
              className="font-sans text-s12 leading-h15 tracking-givmo text-text-tertiary"
              {...testIdProp(`${PREFIX}-preflight-advisory`)}
            >
              {PREFLIGHT_ADVISORY_COPY}
            </p>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
